<template>
  <div
    class="loading-bar pb-loading-bar"
    :class="{ 'pb-loading-bar-hidden': hidden }"
    :style="style"
  />
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue'
import useDelay from '@nicolaudie-group/ng-ui-kit/composables/useDelay'
import useRootStore from '@/stores/root.store'

const rootStore = useRootStore()
const { delay: delayHiding, reset: preventHiding } = useDelay()
const { delay: delayEnable } = useDelay()

const toLoadCount = computed(() => rootStore.toLoadCount)
const loadedCount = computed(() => rootStore.loadedCount)

const currentProgress = ref(0)
const hidden = computed(() => currentProgress.value === 0)

// Offset to show progress even when nothing has finished loading yet
const startOffset = 30
const allowDecreaseUntil = 98

const enabled = ref(true)

function updateProgress() {
  if (enabled.value) {
    if (toLoadCount.value !== 0) {
      // If there is something to load
      // Prevent the loader from hiding in case there is a delay pending
      preventHiding()
      // Calculate the size in % of each step of the bar
      // We substract a part of the start offset from the size of each step
      const stepSizes = (100 / toLoadCount.value) - (startOffset / (toLoadCount.value))
      // Update the progress
      const newProgress = startOffset + (loadedCount.value * stepSizes)
      if (newProgress > currentProgress.value) {
        // If the progress increases, update it
        currentProgress.value = newProgress
      } else if (currentProgress.value > allowDecreaseUntil) {
        // If the progress decreases but we're close to 100%
        // Make room by decreasing the progress
        currentProgress.value = allowDecreaseUntil
      }
      // Otherwise if the progress decreases,
      // ignore the change it and keep the current progress
    } else {
      // If there is nothing (left) to load
      // Set the progress to 100%
      currentProgress.value = 100
      enabled.value = false
      // Reset the progress after 500ms (= loader hiding)
      delayHiding(() => {
        currentProgress.value = 0
        // Wait at least 300ms before showing the progress again
        delayEnable(() => {
          enabled.value = true
          // If there is something to load when we re-enable
          if (toLoadCount.value !== 0) {
            // Make sure to update the progress
            updateProgress()
          }
        }, 300)
      }, 600)
    }
  }
}

watch([toLoadCount, loadedCount], updateProgress)

const style = computed(() => ({
  width: `${currentProgress.value}%`,
}))
</script>

<style lang="scss">
.pb-loading-bar.loading-bar {
  transform-origin: top left;
  transform: scaleY(1);
  transition:
    transform 0s ease, // No transition, no delay when appearing
    width .5s ease; // .5s transition, no delay when growing

  &.pb-loading-bar-hidden {
    transform: scaleY(0);
    transition:
      transform .2s ease, // .2s transition, no delay when disappearing
      width 0s ease .2s; // No transition, 0.2s delay (reset to 0 after the transform transition)
  }
}
</style>
